import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Badge,
  Grid,
  Card,
  ThemeIcon,
  Table,
  Pagination,
  Stack,
  ActionIcon,
  Menu,
  Skeleton,
  Avatar,
  Box,
  RingProgress,
  Center,
  Divider,
  Progress,
  Modal,
  Tabs,
  Code,
  Textarea,
  TextInput,
  Select,
  Popover,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconClock,
  IconChecklist,
  IconUser,
  IconCalendar,
  IconEdit,
  IconHeadphones,
  IconStar,
  IconMessage,
  IconActivity,
  IconId,
  IconBan,
  IconShieldCheck,
  IconRefresh,
  IconX,
  IconPhoto,
  IconLink,
  IconExternalLink,
  IconCheck,
  IconBolt,
  IconTrophy,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import {
  usePerformRequest,
  PerformRequest,
} from "../../../../hooks/usePerformRequest";
import { PaginatedResponse } from "../../../../lib/types";
import { IUserProps } from "../../../../lib/types/User";

// Types
type AttemptStatus =
  | "in_progress"
  | "completed"
  | "failed"
  | "pending_verification";

interface Attempt {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
    level?: number;
  };
  missionId: string;
  status: AttemptStatus;
  listenCount: number;
  startedAt: string;
  missionStartTime?: string;
  completedAt?: string;
  rating?: number;
  comment?: string;
  updatedAt: string;
  proof?: {
    id: string;
    platform: string;
    proofUrl?: string;
    postLink?: string;
    status: "pending" | "approved" | "rejected";
    submittedAt: string;
    rejectionReason?: string;
  };
}

interface Mission {
  id: string; // API returns 'id'
  title: string;
  description: string;
  type: string;
  actionType?: string;
  spotifyUrl?: string;
  youtubeUrl?: string;
  platformUrl?: string;
  requiredListens?: number;
  points: number;
  currency?: string;
  status: "active" | "completed" | "draft";
  completions: number;
  targetCompletions: number;
  startDate?: string;
  expiresAt?: string;
  createdAt: string;
  verificationInstruction?: string;
  requiresProofPost?: boolean;
  minWatchDurationSeconds?: number;
  watchToEnd?: boolean;
  cooldownSeconds?: number;
  platforms?: string[];
  platformRule?: "any" | "all";
  minLevel?: number;
}

const MissionDetails = () => {
  const navigate = useNavigate();
  const { missionId } = useParams<{ missionId: string }>();

  const [opened, { open, close }] = useDisclosure(false);
  const [selectedAttempt, setSelectedAttempt] = useState<Attempt | null>(null);

  // Full User Profile fetching for selected attempt
  const userProfileEndpoint = useMemo(
    () =>
      selectedAttempt?.userId?._id
        ? Endpoints.GetUserProfile(selectedAttempt.userId._id)
        : { route: "", method: "GET" as const },
    [selectedAttempt],
  );

  const { data: userProfileResponse, isLoading: userProfileLoading } =
    usePerformRequest<{ data: IUserProps }>({
      route: userProfileEndpoint.route,
      method: "GET",
    });

  const userProfile = userProfileResponse?.data;

  // Revocation Modal State
  const [revokeModalOpened, setRevokeModalOpened] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");
  const [isRevoking, setIsRevoking] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Proof Review State
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvePopoverOpen, setApprovePopoverOpen] = useState(false);

  // State for attempts pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);

  // Fetch Mission Details
  // If missionId is undefined, route is invalid. The hook handles it but ideally we should only fetch if missionId exists.
  // Since usePerformRequest doesn't have skip, we pass a potentially invalid route, or perform a check before rendering.
  // Actually, useParams ensures missionId is string or undefined. If undefined we can redirect or show error.

  const missionRoute = missionId ? Endpoints.GetMission(missionId).route : "";
  const {
    data: missionResponse,
    isLoading: missionLoading,
    reloadData: reloadMission,
  } = usePerformRequest<{
    data: Mission;
  }>({
    route: missionRoute,
    method: "GET",
  });

  // Fetch Attempts
  const attemptsEndpoint = useMemo(
    () =>
      missionId
        ? Endpoints.GetMissionAttempts(missionId, {
            page: currentPage,
            limit: pageSize,
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
          })
        : { route: "", method: "GET" as const },
    [missionId, currentPage, pageSize, debouncedSearch, statusFilter],
  );

  const {
    data: attemptsResponse,
    isLoading: attemptsLoading,
    reloadData: reloadAttempts,
  } = usePerformRequest<PaginatedResponse<Attempt[]>>({
    route: attemptsEndpoint.route,
    method: "GET",
  });

  const handleReload = async () => {
    setIsReloading(true);
    await Promise.all([
      reloadMission && reloadMission(),
      reloadAttempts && reloadAttempts(),
    ]);
    setTimeout(() => setIsReloading(false), 500); // Small delay for visual feedback
  };

  const mission = missionResponse?.data;

  useEffect(() => {
    if (mission?.title) {
      document.title = `${mission.title} | Mission Details | Virel Admin`;
    } else {
      document.title = "Mission Details | Virel Admin";
    }
  }, [mission]);

  // Attempts Handling
  // API returns { data: Attempt[], totalRecords, count, page, limit, message } at top level
  const attempts = Array.isArray(attemptsResponse?.data)
    ? attemptsResponse.data
    : [];
  const totalAttempts = attemptsResponse?.totalRecords ?? attempts.length ?? 0;
  const totalPages = Math.ceil(totalAttempts / pageSize) || 1;

  if (missionLoading || isReloading) {
    return (
      <Container size="xl" py="xl">
        <Skeleton height={50} width={200} mb="xl" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Skeleton height={200} mb="md" />
            <Skeleton height={400} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton height={300} mb="md" />
            <Skeleton height={200} />
          </Grid.Col>
        </Grid>
      </Container>
    );
  }

  if (!mission) {
    return (
      <Container size="xl" py="xl">
        <Title>Activity Not Found</Title>
        <Button mt="md" onClick={() => navigate("/d/missions")}>
          Back to Activities
        </Button>
      </Container>
    );
  }

  // Helper to extract Spotify ID
  const getSpotifyId = (url: string) => {
    try {
      if (!url) return null;
      if (url.startsWith("spotify:track:")) return url.split(":")[2];
      const match = url.match(/track\/([a-zA-Z0-9]+)/);
      return match ? match[1] : null;
    } catch {
      return null;
    }
  };

  const spotifyId = getSpotifyId(mission.spotifyUrl || "");

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge color="green" size="lg">
            Active
          </Badge>
        );
      case "completed":
        return (
          <Badge color="teal" size="lg">
            Completed
          </Badge>
        );
      case "draft":
        return (
          <Badge color="gray" size="lg">
            Draft
          </Badge>
        );
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  const getAttemptStatusBadge = (status: AttemptStatus) => {
    switch (status) {
      case "completed":
        return (
          <Badge color="green" variant="light">
            Completed
          </Badge>
        );
      case "in_progress":
        return (
          <Badge color="green" variant="light">
            In Progress
          </Badge>
        );
      case "pending_verification":
        return (
          <Badge color="yellow" variant="light">
            Pending Review
          </Badge>
        );
      case "failed":
        return (
          <Badge color="red" variant="light">
            Failed
          </Badge>
        );
      default:
        return (
          <Badge color="gray" variant="light">
            {status}
          </Badge>
        );
    }
  };

  const completionPercentage =
    mission.targetCompletions > 0
      ? Math.min((mission.completions / mission.targetCompletions) * 100, 100)
      : 0;

  const handleRowClick = (attempt: Attempt) => {
    setSelectedAttempt(attempt);
    open();
  };

  // ... (existing code)

  const openRevokeModal = () => {
    setRevokeReason("Violated guidelines"); // Default reason
    setRevokeModalOpened(true);
  };

  const confirmRevoke = async () => {
    if (!selectedAttempt || !mission) return;
    if (!revokeReason.trim()) {
      setErrorMessage("Please provide a reason for revocation.");
      setErrorModalOpen(true);
      return;
    }

    setIsRevoking(true);
    try {
      const endpoint = Endpoints.RevokeMissionAttempt(
        mission.id,
        selectedAttempt._id,
      );
      const res = await PerformRequest({
        ...endpoint,
        data: { reason: revokeReason },
      });

      if (res.status === 200) {
        reloadAttempts();
        setRevokeModalOpened(false);
        close(); // Close the attempts details modal too if desired
      } else {
        setErrorMessage(res.message || "Failed to revoke attempt");
        setErrorModalOpen(true);
      }
    } catch (e) {
      setErrorMessage("An unexpected error occurred");
      setErrorModalOpen(true);
    } finally {
      setIsRevoking(false);
    }
  };

  const handleApproveProof = async () => {
    if (!selectedAttempt?.proof) return;

    setIsApproving(true);
    try {
      const endpoint = Endpoints.ApproveProof(selectedAttempt.proof.id);
      const res = await PerformRequest({
        ...endpoint,
        data: { status: "approved" },
      });

      if (res.status === 200) {
        reloadAttempts();
        reloadMission();
        close();
      } else {
        setErrorMessage(res.message || "Failed to approve proof");
        setErrorModalOpen(true);
      }
    } catch (e) {
      setErrorMessage("An unexpected error occurred");
      setErrorModalOpen(true);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectProof = async () => {
    if (!selectedAttempt?.proof) return;
    if (!rejectionReason.trim()) {
      setErrorMessage("Please provide a reason for rejection.");
      setErrorModalOpen(true);
      return;
    }

    setIsRejecting(true);
    try {
      const endpoint = Endpoints.RejectProof(selectedAttempt.proof.id);
      const res = await PerformRequest({
        ...endpoint,
        data: { status: "rejected", rejectionReason: rejectionReason.trim() },
      });

      if (res.status === 200) {
        reloadAttempts();
        setRejectModalOpened(false);
        close();
      } else {
        setErrorMessage(res.message || "Failed to reject proof");
        setErrorModalOpen(true);
      }
    } catch (e) {
      setErrorMessage("An unexpected error occurred");
      setErrorModalOpen(true);
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Modal
        opened={opened}
        onClose={close}
        title={<Title order={3}>Attempt Details</Title>}
        size="lg"
      >
        {selectedAttempt && (
          <Tabs defaultValue="details">
            <Tabs.List mb="md">
              <Tabs.Tab
                value="details"
                leftSection={<IconActivity size={14} />}
              >
                Attempt Info
              </Tabs.Tab>
              <Tabs.Tab value="user" leftSection={<IconUser size={14} />}>
                User Info
              </Tabs.Tab>
              {(mission?.requiresProofPost || selectedAttempt.proof) && (
                <Tabs.Tab value="proof" leftSection={<IconPhoto size={14} />}>
                  Proof Submission
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value="details">
              <Stack>
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    Status
                  </Text>
                  {getAttemptStatusBadge(selectedAttempt.status)}
                </Group>

                {selectedAttempt.status === "completed" && (
                  <Button
                    color="red"
                    variant="light"
                    leftSection={<IconBan size={16} />}
                    onClick={openRevokeModal}
                    fullWidth
                    display="none"
                  >
                    Revoke & Refund
                  </Button>
                )}

                {["spotify", "youtube"].includes(mission.type) && (
                  <>
                    <Divider />
                    <Group justify="space-between">
                      <Text fw={500} c="dimmed">
                        Progress
                      </Text>
                      <Group gap="xs">
                        <Text fw={700}>
                          {selectedAttempt.listenCount} /{" "}
                          {mission?.requiredListens}
                        </Text>
                        <Progress
                          value={
                            (selectedAttempt.listenCount /
                              (mission?.requiredListens || 1)) *
                            100
                          }
                          size="sm"
                          w={100}
                          color={
                            selectedAttempt.status === "completed"
                              ? "green"
                              : "green"
                          }
                        />
                      </Group>
                    </Group>
                  </>
                )}
                <Divider />
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    Attempt ID
                  </Text>
                  <Code>{selectedAttempt._id}</Code>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    Started At
                  </Text>
                  <Text>
                    {new Date(selectedAttempt.startedAt).toLocaleString()}
                  </Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500} c="dimmed">
                    Last Updated
                  </Text>
                  <Text>
                    {new Date(selectedAttempt.updatedAt).toLocaleString()}
                  </Text>
                </Group>
                {selectedAttempt.completedAt && (
                  <>
                    <Divider />
                    <Group justify="space-between">
                      <Text fw={500} c="dimmed">
                        Completed At
                      </Text>
                      <Text>
                        {new Date(selectedAttempt.completedAt).toLocaleString()}
                      </Text>
                    </Group>
                  </>
                )}
                {(selectedAttempt.rating || selectedAttempt.comment) && (
                  <>
                    <Divider />
                    <Text fw={700} mt="sm">
                      Feedback
                    </Text>
                    {selectedAttempt.rating && (
                      <Group>
                        <IconStar size={16} fill="orange" color="orange" />
                        <Text>{selectedAttempt.rating} Stars</Text>
                      </Group>
                    )}
                    {selectedAttempt.comment && (
                      <Paper withBorder p="sm" bg="var(--bg-surface-2)">
                        <Text size="sm">{selectedAttempt.comment}</Text>
                      </Paper>
                    )}
                  </>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="user">
              <Stack mt="md">
                {userProfileLoading ? (
                  <Stack align="center" mt="md">
                    <Skeleton height={80} circle mb="md" />
                    <Skeleton height={20} width={150} mb="xs" />
                    <Skeleton height={15} width={200} mb="md" />
                    <Skeleton height={30} width={100} mb="xl" />

                    <Grid w="100%" mt="md">
                      <Grid.Col span={4}>
                        <Skeleton height={60} />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Skeleton height={60} />
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Skeleton height={60} />
                      </Grid.Col>
                    </Grid>
                    <Skeleton height={150} w="100%" mt="md" />
                  </Stack>
                ) : userProfile ? (
                  <>
                    <Stack align="center" mt="md" gap={4}>
                      <Avatar size="xl" radius="xl" color="initials" mb="xs">
                        {userProfile.firstName?.[0]}
                        {userProfile.lastName?.[0]}
                      </Avatar>
                      <Title order={3}>
                        {userProfile.firstName} {userProfile.lastName}
                      </Title>
                      <Text c="dimmed">{userProfile.email}</Text>
                      {userProfile.phoneNumber && (
                        <Text c="dimmed" size="sm">
                          {userProfile.phoneNumber}
                        </Text>
                      )}

                      <Group mt="md">
                        <Badge
                          variant="light"
                          size="lg"
                          leftSection={<IconId size={14} />}
                        >
                          {userProfile.id}
                        </Badge>
                        {userProfile.monetizationStatus && (
                          <Badge
                            color={
                              userProfile.monetizationStatus === "monetized"
                                ? "green"
                                : "red"
                            }
                            variant="light"
                            size="lg"
                          >
                            {userProfile.monetizationStatus.toUpperCase()}
                          </Badge>
                        )}
                        <Badge
                          color="yellow"
                          variant="filled"
                          leftSection={<IconTrophy size={14} />}
                          size="lg"
                        >
                          Level {userProfile.level ?? 1}
                        </Badge>
                      </Group>
                    </Stack>

                    <Divider my="md" />

                    <Grid>
                      <Grid.Col span={6}>
                        <Paper withBorder p="sm" radius="md">
                          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                            Balance
                          </Text>
                          <Text size="xl" fw={700}>
                            ₦{userProfile.balance?.toLocaleString() || 0}
                          </Text>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Paper withBorder p="sm" radius="md">
                          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                            Total Earned
                          </Text>
                          <Text size="xl" fw={700}>
                            {userProfile.totalCoinsEarned?.toLocaleString() ||
                              0}
                          </Text>
                        </Paper>
                      </Grid.Col>
                    </Grid>

                    <Paper withBorder p="md" radius="md" mt="sm">
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Text size="sm" fw={500} c="dimmed">
                            Joined
                          </Text>
                          <Text size="sm">
                            {userProfile.createdAt
                              ? new Date(
                                  userProfile.createdAt,
                                ).toLocaleDateString()
                              : "N/A"}
                          </Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" fw={500} c="dimmed">
                            Referral Code
                          </Text>
                          <Code>{userProfile.referralCode || "N/A"}</Code>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" fw={500} c="dimmed">
                            Monetized
                          </Text>
                          <Badge
                            color={
                              userProfile.monetizationStatus === "monetized"
                                ? "green"
                                : "gray"
                            }
                          >
                            {userProfile.monetizationStatus || "Pending"}
                          </Badge>
                        </Group>
                      </Stack>
                    </Paper>

                    <Button
                      variant="light"
                      mt="md"
                      fullWidth
                      rightSection={<IconExternalLink size={16} />}
                      onClick={() => {
                        close();
                        navigate(`/d/users/${userProfile.id}`);
                      }}
                    >
                      View Full Profile
                    </Button>
                  </>
                ) : (
                  <Center py="xl">
                    <Text c="dimmed">Could not load user profile.</Text>
                  </Center>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="proof">
              {selectedAttempt.proof ? (
                <Stack>
                  <Group justify="space-between">
                    <Text fw={500} c="dimmed">
                      Status
                    </Text>
                    <Badge
                      color={
                        selectedAttempt.proof.status === "pending"
                          ? "yellow"
                          : selectedAttempt.proof.status === "approved"
                            ? "green"
                            : "red"
                      }
                      variant="light"
                    >
                      {selectedAttempt.proof.status.toUpperCase()}
                    </Badge>
                  </Group>
                  <Divider />
                  <Group justify="space-between">
                    <Text fw={500} c="dimmed">
                      Platform
                    </Text>
                    <Badge variant="light">
                      {selectedAttempt.proof.platform}
                    </Badge>
                  </Group>
                  <Divider />
                  <Group justify="space-between">
                    <Text fw={500} c="dimmed">
                      Submitted At
                    </Text>
                    <Text>
                      {new Date(
                        selectedAttempt.proof.submittedAt,
                      ).toLocaleString()}
                    </Text>
                  </Group>

                  {selectedAttempt.proof.proofUrl && (
                    <>
                      <Divider />
                      <Text fw={700} mt="sm">
                        Screenshot Proof
                      </Text>
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between" align="center">
                          <Group gap="sm">
                            <IconPhoto size={20} />
                            <Text size="sm" truncate maw={300}>
                              {selectedAttempt.proof.proofUrl}
                            </Text>
                          </Group>
                          <Button
                            size="xs"
                            variant="subtle"
                            leftSection={<IconExternalLink size={14} />}
                            component="a"
                            href={selectedAttempt.proof.proofUrl}
                            target="_blank"
                          >
                            View
                          </Button>
                        </Group>
                      </Paper>

                      <Divider mt="md" />
                      <Box mt="sm">
                        <Text fw={700} mb={8}>
                          Review & Feedback
                        </Text>
                        {selectedAttempt.rating || selectedAttempt.comment ? (
                          <Paper withBorder p="md" bg="var(--bg-surface-2)" radius="md">
                            {selectedAttempt.rating && (
                              <Group gap={4} mb="xs">
                                <Text fw={600} size="sm">
                                  Rating:
                                </Text>
                                <Group gap={2}>
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <IconStar
                                      key={s}
                                      size={16}
                                      fill={
                                        selectedAttempt.rating! >= s
                                          ? "orange"
                                          : "transparent"
                                      }
                                      color={
                                        selectedAttempt.rating! >= s
                                          ? "orange"
                                          : "gray"
                                      }
                                    />
                                  ))}
                                </Group>
                              </Group>
                            )}
                            {selectedAttempt.comment && (
                              <Box>
                                <Text fw={600} size="sm" mb={4}>
                                  Track Opinion:
                                </Text>
                                <Text size="sm" style={{ fontStyle: "italic" }}>
                                  "{selectedAttempt.comment}"
                                </Text>
                              </Box>
                            )}
                          </Paper>
                        ) : (
                          <Text size="sm" c="dimmed">
                            No feedback provided.
                          </Text>
                        )}
                      </Box>
                    </>
                  )}

                  {selectedAttempt.proof.postLink && (
                    <>
                      <Divider />
                      <Text fw={700} mt="sm">
                        Post Link
                      </Text>
                      <Paper withBorder p="sm" radius="md">
                        <Group justify="space-between" align="center">
                          <Group gap="sm">
                            <IconLink size={20} />
                            <Text size="sm" truncate maw={300}>
                              {selectedAttempt.proof.postLink}
                            </Text>
                          </Group>
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() =>
                              window.open(
                                selectedAttempt.proof!.postLink,
                                "_blank",
                              )
                            }
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    </>
                  )}

                  {selectedAttempt.proof.rejectionReason && (
                    <>
                      <Divider />
                      <Alert color="red" title="Rejection Reason" mt="sm" variant="light">
                        {selectedAttempt.proof.rejectionReason}
                      </Alert>
                    </>
                  )}

                  {/* Approve/Reject Buttons - Only show for pending proofs */}
                  {selectedAttempt.proof.status === "pending" && (
                    <>
                      <Divider my="md" />
                      <Group justify="flex-end">
                        <Button
                          variant="light"
                          color="red"
                          leftSection={<IconX size={16} />}
                          onClick={() => {
                            setRejectionReason("");
                            setRejectModalOpened(true);
                          }}
                          disabled={isApproving || isRejecting}
                        >
                          Reject
                        </Button>
                        <Popover
                          opened={approvePopoverOpen}
                          onClose={() => setApprovePopoverOpen(false)}
                          position="bottom"
                          withArrow
                          shadow="md"
                          width={240}
                        >
                          <Popover.Target>
                            <Button
                              color="green"
                              leftSection={<IconCheck size={16} />}
                              onClick={() => setApprovePopoverOpen(true)}
                              loading={isApproving}
                              disabled={isRejecting}
                            >
                              Approve
                            </Button>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <Stack gap="xs">
                              <Text size="sm" fw={500}>
                                Confirm Approval
                              </Text>
                              <Text size="xs" c="dimmed">
                                This will credit the user's balance. Continue?
                              </Text>
                              <Group justify="flex-end" gap="xs">
                                <Button
                                  size="compact-xs"
                                  variant="default"
                                  onClick={() => setApprovePopoverOpen(false)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="compact-xs"
                                  color="green"
                                  loading={isApproving}
                                  onClick={async () => {
                                    await handleApproveProof();
                                    setApprovePopoverOpen(false);
                                  }}
                                >
                                  Confirm
                                </Button>
                              </Group>
                            </Stack>
                          </Popover.Dropdown>
                        </Popover>
                      </Group>
                    </>
                  )}
                </Stack>
              ) : (
                <Center py="xl">
                  <Stack align="center" gap="xs">
                    <IconPhoto size={48} style={{ opacity: 0.3 }} />
                    <Text c="dimmed">No proof submitted yet</Text>
                  </Stack>
                </Center>
              )}
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>

      <Modal
        opened={revokeModalOpened}
        onClose={() => setRevokeModalOpened(false)}
        title="Revoke & Refund Activity"
        centered
      >
        <Text size="sm" mb="md">
          Are you sure you want to revoke this completion? This will debit the
          user's balance. Please provide a reason for the user.
        </Text>
        <Textarea
          label="Reason for revocation"
          placeholder="e.g. Violated guidelines"
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.currentTarget.value)}
          minRows={3}
          mb="md"
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => setRevokeModalOpened(false)}
            disabled={isRevoking}
          >
            Cancel
          </Button>
          <Button color="red" onClick={confirmRevoke} loading={isRevoking}>
            Confirm Revoke
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={rejectModalOpened}
        onClose={() => setRejectModalOpened(false)}
        title="Reject Proof"
        centered
      >
        <Text size="sm" mb="md">
          Please provide a reason for rejecting this proof. This will be sent to
          the user.
        </Text>
        <Textarea
          label="Rejection Reason"
          placeholder="e.g. Screenshot does not show completed task"
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.currentTarget.value)}
          minRows={3}
          mb="md"
          error={!rejectionReason.trim() && "Reason is required"}
        />
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => setRejectModalOpened(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleRejectProof}
            loading={isRejecting}
            disabled={!rejectionReason.trim()}
          >
            Confirm Reject
          </Button>
        </Group>
      </Modal>

      {/* Header */}
      <Group justify="space-between" align="flex-start" mb="xl">
        <div>
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/d/missions")}
            mb="xs"
            pl={0}
          >
            Back to Activities
          </Button>
          <Group align="center" gap="md">
            <Title order={1}>{mission.title}</Title>
            {getStatusBadge(mission.status)}
          </Group>
          <Text c="dimmed" size="lg" mt={4} maw={600}>
            {mission.description}
          </Text>
        </div>
        <Group>
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            loading={isReloading}
            onClick={handleReload}
          >
            Reload
          </Button>
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() => navigate(`/d/missions/update/${mission.id}`)}
          >
            Edit Activity
          </Button>
        </Group>
      </Group>

      <Grid gutter="xl">
        {/* Main Content */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          {/* Stats Overview */}
          <Grid mb="xl">
            <Grid.Col span={4}>
              <Card withBorder padding="lg" radius="md">
                <Group justify="space-between">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Total Attempts
                  </Text>
                  <ThemeIcon
                    size="lg"
                    radius="md"
                    variant="light"
                    color="green"
                  >
                    <IconUser size={20} />
                  </ThemeIcon>
                </Group>
                <Text size="2xl" fw={700} mt="sm">
                  {totalAttempts}
                </Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={4}>
              <Card withBorder padding="lg" radius="md">
                <Group justify="space-between">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Completions
                  </Text>
                  <ThemeIcon
                    size="lg"
                    radius="md"
                    variant="light"
                    color="green"
                  >
                    <IconChecklist size={20} />
                  </ThemeIcon>
                </Group>
                <Text size="2xl" fw={700} mt="sm">
                  {mission.completions}
                </Text>
              </Card>
            </Grid.Col>
            <Grid.Col span={4}>
              <Card withBorder padding="lg" radius="md">
                <Group justify="space-between">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Points
                  </Text>
                  <ThemeIcon
                    size="lg"
                    radius="md"
                    variant="light"
                    color="orange"
                  >
                    <IconStar size={20} />
                  </ThemeIcon>
                </Group>
                <Text size="2xl" fw={700} mt="sm">
                  {mission.points}
                </Text>
              </Card>
            </Grid.Col>
          </Grid>

          {/* User Attempts Table */}
          <Paper withBorder radius="md" p="md">
            <Group justify="space-between" mb="md">
              <div>
                <Title order={3}>Recent Activity</Title>
                <Text c="dimmed" size="sm">
                  Users attempting this mission
                </Text>
              </div>
              <Group>
                <TextInput
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    setCurrentPage(1);
                  }}
                  w={200}
                />
                <Select
                  placeholder="Filter Status"
                  value={statusFilter}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(1);
                  }}
                  clearable
                  data={[
                    { value: "completed", label: "Completed" },
                    { value: "in_progress", label: "In Progress" },
                    { value: "pending_verification", label: "Pending Review" },
                    { value: "failed", label: "Failed" },
                  ]}
                  w={160}
                />
              </Group>
            </Group>

            <Table verticalSpacing="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Progress</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Review</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {attemptsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={40} circle mb="sm" />
                        <Skeleton height={8} width="100px" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={20} width="80px" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={8} width="50px" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={8} width="80px" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={8} width="40px" />
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : attempts.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5} align="center">
                      <Text c="dimmed" py="xl">
                        No attempts yet
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  attempts.map((attempt: Attempt) => (
                    <Table.Tr
                      key={attempt._id}
                      onClick={() => handleRowClick(attempt)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Td>
                        <Group gap="sm">
                          <Avatar
                            src={attempt.userId.profileImage}
                            radius="xl"
                            color="initials"
                          >
                            {attempt.userId.firstName[0]}
                            {attempt.userId.lastName[0]}
                          </Avatar>
                          <div>
                            <Text size="sm" fw={500}>
                              {attempt.userId.firstName}{" "}
                              {attempt.userId.lastName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {attempt.userId.email}
                            </Text>
                            <Badge
                              size="xs"
                              variant="light"
                              color="yellow"
                              mt={4}
                            >
                              Level {attempt.userId.level ?? 1}
                            </Badge>
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {getAttemptStatusBadge(attempt.status)}
                      </Table.Td>
                      <Table.Td>
                        {["spotify", "youtube"].includes(mission.type) ? (
                          <>
                            <Group gap="xs">
                              <IconHeadphones
                                size={14}
                                style={{ opacity: 0.5 }}
                              />
                              <Text size="sm">
                                {attempt.listenCount} /{" "}
                                {mission.requiredListens}
                              </Text>
                            </Group>
                            <Progress
                              value={
                                (attempt.listenCount /
                                  (mission.requiredListens || 1)) *
                                100
                              }
                              size="xs"
                              mt={4}
                              color={
                                attempt.status === "completed"
                                  ? "green"
                                  : "blue"
                              }
                            />
                          </>
                        ) : (
                          <Text size="sm" c="dimmed">
                            -
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(attempt.startedAt).toLocaleDateString()}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(attempt.startedAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {attempt.rating ? (
                          <Group gap={4}>
                            <IconStar size={14} fill="orange" color="orange" />
                            <Text size="sm">{attempt.rating}</Text>
                            {attempt.comment && (
                              <Menu shadow="md" width={200}>
                                <Menu.Target>
                                  <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    color="gray"
                                  >
                                    <IconMessage size={14} />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Label>Comment</Menu.Label>
                                  <Text size="sm" p="xs">
                                    {attempt.comment}
                                  </Text>
                                </Menu.Dropdown>
                              </Menu>
                            )}
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">
                            -
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" mt="lg">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                />
              </Group>
            )}
          </Paper>
        </Grid.Col>

        {/* Sidebar Info */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Card withBorder radius="md" p="xl">
              <Text fz="xs" tt="uppercase" fw={700} c="dimmed" mb="md">
                Overall Progress
              </Text>
              <Center>
                <RingProgress
                  size={180}
                  roundCaps
                  thickness={16}
                  sections={[
                    {
                      value: completionPercentage,
                      color: completionPercentage >= 100 ? "teal" : "brand",
                    },
                  ]}
                  label={
                    <Center>
                      <Stack align="center" gap={0}>
                        <Text fz="xl" fw={700} ta="center">
                          {Math.round(completionPercentage)}%
                        </Text>
                        <Text fz="xs" c="dimmed" ta="center">
                          Completed
                        </Text>
                      </Stack>
                    </Center>
                  }
                />
              </Center>
            </Card>

            <Card withBorder radius="md" p="md">
              <Text fz="xs" tt="uppercase" fw={700} c="dimmed" mb="md">
                Activity Details
              </Text>

              <Stack gap="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconCalendar size={14} />
                    </ThemeIcon>
                    <Text size="sm">Created</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {new Date(mission.createdAt).toLocaleDateString()}
                  </Text>
                </Group>

                <Divider />

                {mission.startDate && (
                  <>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon color="gray" variant="light" size="sm">
                          <IconCalendar size={14} />
                        </ThemeIcon>
                        <Text size="sm">Starts</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {new Date(mission.startDate).toLocaleDateString()}
                      </Text>
                    </Group>
                    <Divider />
                  </>
                )}

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconClock size={14} />
                    </ThemeIcon>
                    <Text size="sm">Expires</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {mission.expiresAt
                      ? new Date(mission.expiresAt).toLocaleDateString()
                      : "Never"}
                  </Text>
                </Group>

                <Divider />

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconActivity size={14} />
                    </ThemeIcon>
                    <Text size="sm">Activity Type</Text>
                  </Group>
                  <Badge
                    color="blue"
                    variant="light"
                    style={{ textTransform: "capitalize" }}
                  >
                    {mission.type}
                  </Badge>
                </Group>

                <Divider />

                {mission.actionType && (
                  <>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon color="gray" variant="light" size="sm">
                          <IconBolt size={14} />
                        </ThemeIcon>
                        <Text size="sm">Action</Text>
                      </Group>
                      <Badge
                        color="violet"
                        variant="light"
                        style={{ textTransform: "capitalize" }}
                      >
                        {mission.actionType}
                      </Badge>
                    </Group>
                    <Divider />
                  </>
                )}

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconStar size={14} />
                    </ThemeIcon>
                    <Text size="sm">Reward</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {mission.points} {mission.currency || "COIN"}
                  </Text>
                </Group>

                <Divider />

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconChecklist size={14} />
                    </ThemeIcon>
                    <Text size="sm">Target Completions</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {mission.targetCompletions}
                  </Text>
                </Group>

                <Divider />

                {mission.requiredListens ? (
                  <>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon color="gray" variant="light" size="sm">
                          <IconHeadphones size={14} />
                        </ThemeIcon>
                        <Text size="sm">Required Listens</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {mission.requiredListens}
                      </Text>
                    </Group>
                    <Divider />
                  </>
                ) : null}

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconPhoto size={14} />
                    </ThemeIcon>
                    <Text size="sm">Requires Proof</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    {mission.requiresProofPost ? "Yes" : "No"}
                  </Text>
                </Group>

                <Divider />

                <Group justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon color="gray" variant="light" size="sm">
                      <IconStar size={14} />
                    </ThemeIcon>
                    <Text size="sm">Minimum Level</Text>
                  </Group>
                  <Text size="sm" fw={500}>
                    Level {mission.minLevel || 1}
                  </Text>
                </Group>

                {mission.verificationInstruction && (
                  <Box>
                    <Text size="xs" c="dimmed" mt={4}>
                      Instructions
                    </Text>
                    <Text size="sm" mt={2} style={{ whiteSpace: "pre-wrap" }}>
                      {mission.verificationInstruction}
                    </Text>
                  </Box>
                )}

                <Divider />

                {(mission.spotifyUrl ||
                  mission.youtubeUrl ||
                  mission.platformUrl) && (
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>
                      Target URL
                    </Text>
                    <Text
                      size="xs"
                      component="a"
                      href={
                        mission.spotifyUrl ||
                        mission.youtubeUrl ||
                        mission.platformUrl
                      }
                      target="_blank"
                      c="blue"
                      style={{ wordBreak: "break-all" }}
                    >
                      {mission.spotifyUrl ||
                        mission.youtubeUrl ||
                        mission.platformUrl}
                    </Text>
                  </Box>
                )}

                {mission.type === "youtube" &&
                  mission.actionType === "watch" && (
                    <>
                      <Divider />
                      <Box>
                        <Text size="xs" c="dimmed" mb="sm" fw={700}>
                          Video Settings
                        </Text>
                        <Stack gap={4}>
                          <Group justify="space-between">
                            <Text size="xs">Min Duration</Text>
                            <Text size="xs" fw={500}>
                              {mission.minWatchDurationSeconds || 0}s
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text size="xs">Wait To End</Text>
                            <Text size="xs" fw={500}>
                              {mission.watchToEnd ? "Yes" : "No"}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text size="xs">Cooldown</Text>
                            <Text size="xs" fw={500}>
                              {mission.cooldownSeconds || 0}s
                            </Text>
                          </Group>
                        </Stack>
                      </Box>
                    </>
                  )}
                {mission.type === "share" && mission.platforms && (
                  <>
                    <Divider />
                    <Box>
                      <Text size="xs" c="dimmed" mb="sm" fw={700}>
                        Share Settings
                      </Text>
                      <Stack gap={4}>
                        <Group justify="space-between">
                          <Text size="xs">Platforms</Text>
                          <Text
                            size="xs"
                            fw={500}
                            style={{ textTransform: "capitalize" }}
                          >
                            {mission.platforms.join(", ")}
                          </Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="xs">Completion Rule</Text>
                          <Text
                            size="xs"
                            fw={500}
                            style={{ textTransform: "capitalize" }}
                          >
                            Must Post on{" "}
                            {mission.platformRule === "all" ? "All" : "Any"}
                          </Text>
                        </Group>
                      </Stack>
                    </Box>
                  </>
                )}

                {spotifyId && (
                  <>
                    <Divider />
                    <Box mt="xs">
                      <Text
                        size="xs"
                        c="dimmed"
                        mb="sm"
                        fw={700}
                        tt="uppercase"
                      >
                        Track Preview
                      </Text>
                      <iframe
                        style={{ borderRadius: "12px" }}
                        src={`https://open.spotify.com/embed/track/${spotifyId}?utm_source=generator`}
                        width="100%"
                        height="152"
                        frameBorder="0"
                        allowFullScreen
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        title="Spotify Embed"
                      ></iframe>
                    </Box>
                  </>
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Error Modal */}
      <Modal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={
          <Group gap="xs">
            <IconX size={20} color="red" />
            <Text fw={700} c="red">
              Error
            </Text>
          </Group>
        }
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text>{errorMessage}</Text>
          <Group justify="flex-end">
            <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default MissionDetails;
